import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Configure axios to always send the session token
axios.interceptors.request.use((config) => {
  const sessionToken = localStorage.getItem("moodpunk_session_token");
  if (sessionToken) {
    config.headers["Authorization"] = `Bearer ${sessionToken}`;
  }
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState(null); // "google" | "moodpunk" | "incognito" | null
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Check for existing session
  const checkAuth = useCallback(async () => {
    const storedMode = localStorage.getItem("moodpunk_auth_mode");
    const sessionToken = localStorage.getItem("moodpunk_session_token");
    
    console.log("[Auth] checkAuth - mode:", storedMode, "hasToken:", !!sessionToken);
    
    if (storedMode === "incognito") {
      setAuthMode("incognito");
      const incognitoId = localStorage.getItem("moodpunk_incognito_id");
      setUser({ 
        name: "Modo Incógnito", 
        email: null, 
        picture: null,
        user_id: incognitoId 
      });
      setLoading(false);
      setInitialized(true);
      return;
    }

    if ((storedMode === "google" || storedMode === "moodpunk") && sessionToken) {
      try {
        console.log("[Auth] Restoring session...");
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setAuthMode(storedMode);
        console.log("[Auth] Session restored:", response.data.user_id);
      } catch (error) {
        console.error("[Auth] Session expired or invalid:", error.response?.status, error.message);
        // Session expired, clear storage
        localStorage.removeItem("moodpunk_auth_mode");
        localStorage.removeItem("moodpunk_session_token");
        setUser(null);
        setAuthMode(null);
      }
    }
    
    setLoading(false);
    setInitialized(true);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login with Google
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Login as Incognito - generate unique ID per browser
  const loginAsIncognito = () => {
    let incognitoId = localStorage.getItem("moodpunk_incognito_id");
    if (!incognitoId) {
      incognitoId = `incognito_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("moodpunk_incognito_id", incognitoId);
    }
    
    localStorage.setItem("moodpunk_auth_mode", "incognito");
    localStorage.removeItem("moodpunk_session_token"); // Clear any existing token
    setAuthMode("incognito");
    setUser({ 
      name: "Modo Incógnito", 
      email: null, 
      picture: null,
      user_id: incognitoId 
    });
  };

  // Register MoodPunk account
  const registerMoodPunk = async (email, password, name, userId = null) => {
    // Get incognito ID if user was in incognito mode (to migrate data)
    const incognitoId = localStorage.getItem("moodpunk_incognito_id");
    
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name,
      user_id: userId, // Custom user ID (optional)
      incognito_id: incognitoId // Will migrate incognito data to new account
    });
    
    // Store token in localStorage
    if (response.data.session_token) {
      localStorage.setItem("moodpunk_session_token", response.data.session_token);
    }
    
    // Clear incognito data after successful migration
    if (response.data.migrated_moods > 0) {
      console.log(`[Auth] Migrated ${response.data.migrated_moods} moods from incognito`);
    }
    localStorage.removeItem("moodpunk_incognito_id");
    
    setUser(response.data.user);
    setAuthMode("moodpunk");
    localStorage.setItem("moodpunk_auth_mode", "moodpunk");
    console.log("[Auth] Registered:", response.data.user.user_id);
    return { ...response.data.user, migrated_moods: response.data.migrated_moods };
  };

  // Check if user ID is available
  const checkUserId = async (userId) => {
    try {
      const response = await axios.get(`${API}/auth/check-id/${encodeURIComponent(userId)}`);
      return response.data;
    } catch (error) {
      return { available: false, error: "Error al verificar ID" };
    }
  };

  // Set user ID (after registration)
  const setUserId = async (userId) => {
    const response = await axios.post(`${API}/auth/set-user-id`, { user_id: userId });
    if (response.data.success) {
      setUser(prev => ({ ...prev, user_id: response.data.user_id }));
    }
    return response.data;
  };

  // Login with MoodPunk account
  const loginMoodPunk = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    
    // Store token in localStorage
    if (response.data.session_token) {
      localStorage.setItem("moodpunk_session_token", response.data.session_token);
    }
    
    setUser(response.data.user);
    setAuthMode("moodpunk");
    localStorage.setItem("moodpunk_auth_mode", "moodpunk");
    console.log("[Auth] Logged in:", response.data.user.user_id);
    return response.data.user;
  };

  // Process OAuth callback
  const processOAuthCallback = async (sessionId) => {
    try {
      console.log("[Auth] Processing OAuth callback with session_id:", sessionId);
      const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
      console.log("[Auth] OAuth response:", response.data);
      
      // Store token in localStorage - THIS IS KEY FOR SYNC
      if (response.data.session_token) {
        localStorage.setItem("moodpunk_session_token", response.data.session_token);
        console.log("[Auth] Token stored for Google user:", response.data.user.user_id);
      } else {
        console.error("[Auth] No session_token in response!");
      }
      
      setUser(response.data.user);
      setAuthMode("google");
      localStorage.setItem("moodpunk_auth_mode", "google");
      return response.data.user;
    } catch (error) {
      console.error("OAuth callback error:", error);
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    if (authMode === "google" || authMode === "moodpunk") {
      try {
        await axios.post(`${API}/auth/logout`, {});
      } catch (error) {
        console.error("Logout error:", error);
      }
    }
    
    localStorage.removeItem("moodpunk_auth_mode");
    localStorage.removeItem("moodpunk_session_token");
    setUser(null);
    setAuthMode(null);
  };

  const value = {
    user,
    authMode,
    loading,
    initialized,
    isAuthenticated: authMode !== null,
    loginWithGoogle,
    loginAsIncognito,
    registerMoodPunk,
    loginMoodPunk,
    checkUserId,
    setUserId,
    processOAuthCallback,
    logout,
    refreshAuth: checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
